import 'animate.css';
import React, { useEffect, useState, useRef } from 'react';

import Dropdown from 'react-bootstrap/Dropdown';
import { useMediaQuery } from 'react-responsive';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { SessionState, logoutUser } from 'features/session/sessionSlice';
import { cleanNextSteps, UserState } from 'features/user/userSlice';
import { navigateTop, userInitials } from 'common/utils';
import data from './navigationData';
import { User } from 'app/types';
import styles from './AppNavbar.module.scss';
import { setFilters } from 'features/session/sessionSlice';
import { cleanFavorites } from 'features/user/userSlice';
import {
  useGetFavoritesAmountQuery,
  useGetCountriesQuery,
  useGetSubjectsQuery,
} from 'services/exploreApi';
import SearchMenu from './SearchMenu';
import UserDashboardSideBarElements from '../UserDashboardSideBarElements/UserDashboardSideBarElements';
import { Expert1 } from 'assets/user';

interface DropdownProps {
  title: string;
  dropdown: {
    main: {
      title: string;
      link: string;
      icon: string;
      badge?: {
        text: string;
        class: string;
      };
      description: string;
    }[];
    other: {
      title: string;
      options: {
        title: string;
        link: string;
      }[];
    }[];
  };
}

const NavBarDropDown = ({
  data,
  closeNavBar,
}: {
  data: DropdownProps;
  closeNavBar: () => void;
}) => {
  const [active, setActive] = useState(false);
  const skipQuery = data.title !== 'Study Abroad';
  const dispatch = useAppDispatch();

  const handleSubjectFilter = (subject_id: number) => {
    dispatch(setFilters({ subject_id: [subject_id] }));
  };

  const { data: countries, isLoading: isCountriesLoading } = useGetCountriesQuery(
    {
      page: 0,
      page_size: 10,
    },
    { skip: skipQuery || !active }
  );
  const { data: subjects, isLoading: isSubjectsLoading } = useGetSubjectsQuery(
    {
      page: 0,
      page_size: 10,
    },
    { skip: skipQuery || !active }
  );

  return (
    <li
      className="nav-item "
      onMouseOver={() => {
        setActive(true);
      }}
      onMouseOut={() => {
        setActive(false);
      }}
    >
      <a
        onClick={() => {
          setActive(true);
        }}
        className={`nav-link p-2 px-md-3  mt-3 mt-md-0 fw-medium position-relative nav-dropdown-toggle rounded-2 ${
          active ? 'active' : ''
        }`}
        href="#"
      >
        {data.title}
      </a>
      <div
        className={`nav-dropdown animate__animated animate__fadeIn animate__faster ${
          active ? 'start-0 top-md-100 d-flex' : 'd-none'
        }`}
      >
        <div className="bg-white rounded-2 d-flex flex-row">
          <ul className="list-unstyled nav-dropdown-main-list py-md-4 px-md-4">
            {data.dropdown.main.map((option, i) => {
              return (
                <li key={i} className="nav-dropdown-item rounded-3 px-2 mb-3 py-3 mt-2">
                  <Link
                    className="d-flex flex-row align-items-center link-unstyled"
                    to={option.link}
                    onClick={closeNavBar}
                  >
                    <div className="nav-dropdown-item-icon rounded-3 p-2 me-3">
                      {' '}
                      <img src={option.icon} />
                    </div>
                    <div className="d-flex flex-column">
                      <h6 className="mb-1">
                        {option.title}{' '}
                        {option.badge && (
                          <span className={`badge ms-2 ${option.badge.class}`}>
                            {option.badge.text}
                          </span>
                        )}
                      </h6>
                      <p className="text-secondary fw-light m-0">
                        <small>{option.description}</small>
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
          {data.dropdown.other.map((list, i) => {
            return (
              <ul
                key={i}
                className="list-unstyled nav-dropdown-secondary-list bg-light py-5 p-4 d-none d-md-inline"
              >
                <li className="nav-dropdown-title text-uppercase fw-semibold mb-2">{list.title}</li>
                {list.title === 'Popular Countries' &&
                  countries?.data.map((country, e) => {
                    return (
                      <li key={e}>
                        <Link to={'#'} onClick={closeNavBar}>
                          {country.name}
                        </Link>
                      </li>
                    );
                  })}
                {list.title === 'Popular Subjects' &&
                  subjects?.data.map((subject, e) => {
                    return (
                      <li key={e}>
                        <Link
                          to={'explore/programs'}
                          onClick={() => handleSubjectFilter(subject.id)}
                        >
                          {subject.name}
                        </Link>
                      </li>
                    );
                  })}
                {list.title !== 'Popular Subjects' &&
                  list.title !== 'Popular Countries' &&
                  list.options?.map((option, e) => {
                    return (
                      <li key={e}>
                        <Link to={option.link} onClick={closeNavBar}>
                          {option.title}
                        </Link>
                      </li>
                    );
                  })}
              </ul>
            );
          })}
        </div>
      </div>
    </li>
  );
};

const NavBarUserDropDown = ({ user, closeNavBar }: { user: User; closeNavBar: () => void }) => {
  const [active, setActive] = useState(false);
  const dispatch = useAppDispatch();
  const logout = () => {
    dispatch(logoutUser());
    navigateTop();
    dispatch(cleanFavorites());
    dispatch(cleanNextSteps());
  };
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();

  const handleRedirectToProfile = () => {
    if (isMobile) {
      navigate('/home');
    }
  };

  return (
    <div
      className="ua-user-menu d-inline ms-2"
      onMouseOver={() => {
        setActive(true);
      }}
      onMouseOut={() => {
        setActive(false);
      }}
    >
      <button
        className={`ua-user-toggle ua-user btn rounded-circle bg-ua-light fw-bold p-1 d-flex align-items-center justify-content-center ${
          active ? 'active' : ''
        } `}
        onClick={handleRedirectToProfile}
      >
        {userInitials(user)}
      </button>
      <div
        className={`nav-dropdown animate__animated animate__fadeIn animate__faster ${
          active ? 'end-0 top-md-100 d-flex' : 'd-none'
        }`}
      >
        <div className="bg-white rounded-2 d-flex flex-row">
          <ul className="list-unstyled nav-dropdown-main-list p-2 py-md-4 px-md-5">
            <li className="d-flex align-items-center flex-row py-2 mb-3">
              <i className="bi bi-person-circle fs-1 me-3" />{' '}
              <div className="d-flex flex-column justify-content-center">
                <h6 className="m-0">{user.name}</h6>{' '}
                <p className="p-0 m-0 text-secondary">
                  <small>{user.email}</small>
                </p>
              </div>
            </li>
            <li className="mb-2">
              <Link
                className="link-unstyled hover-ua-color"
                to="/home/personal_information"
                onClick={closeNavBar}
              >
                <i className="bi bi-person-square me-3" />
                My Profile
              </Link>
            </li>
            <li className="mb-2">
              <Link
                className="link-unstyled hover-ua-color"
                to="/home/preferences"
                onClick={closeNavBar}
              >
                <i className="bi bi-person-gear me-3" />
                My Study Preference
              </Link>
            </li>
            {/* <li className="mb-2">
              <Link className="link-unstyled hover-ua-color" to="" onClick={closeNavBar}>
                <i className="bi bi-file-text me-3" />
                My Documents
              </Link>
            </li> */}
            <li className="mb-2">
              <Link
                className="link-unstyled hover-ua-color"
                to="/home/shorlist"
                onClick={closeNavBar}
              >
                <i className="bi bi-heart me-3" />
                My Shortlist
              </Link>
            </li>
            <li className="mb-2">
              <Link
                className="link-unstyled hover-ua-color"
                to="/home/applications"
                onClick={closeNavBar}
              >
                <i className="bi bi-file-medical me-3" />
                My Applications
              </Link>
            </li>
            <li className="mb-2">
              <Link
                className="link-unstyled hover-ua-color"
                to="personal-statements"
                onClick={closeNavBar}
              >
                <i className="bi bi-file-earmark-medical me-3" />
                My Personal Statements
              </Link>
            </li>
            <hr />
            <li>
              <Link className="link-unstyled hover-ua-color" onClick={logout} to="/">
                <i className="bi bi-box-arrow-right me-3" />
                Logout
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
const NavBar = (): React.ReactElement => {
  const { logged, user } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [dropdownShown, setDropdownShown] = useState<boolean>(false);
  const [favoritesNumber, setFavoritesNumber] = useState<number>(0);
  const [dropdownsShown, setDropdownsShown] = useState<{ [k: string]: boolean }>({
    expert_help: false,
    more: false,
    search: false,
  });

  const { favorites } = useAppSelector(({ user }: { user: UserState }) => user);
  // const [removeLogin] = useRemoveLoginMutation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: favoritesAmount, refetch } = useGetFavoritesAmountQuery(undefined, {
    skip: !logged,
  });

  const showDropdown = (key: string) => {
    setDropdownShown(true);
    setDropdownsShown((sf) => ({ ...sf, [key]: true }));
  };

  const hideDropdown = (key: string) => {
    setDropdownShown(false);
    setDropdownsShown((sf) => ({ ...sf, [key]: false }));
  };

  const navigateTo = (route: string) => {
    navigate(route);
    hideDropdown('search');
  };

  const navigateToFavorite = () => {
    navigate('/home');
    setTimeout(() => {
      navigateTop(0, 0, 'topics');
    }, 100);
  };

  useEffect(() => {
    if (logged) {
      refetch();
      if (favorites && favorites.length > 0) {
        setFavoritesNumber(favorites ? favorites.length : 0);
      } else if (favoritesAmount) {
        setFavoritesNumber(favoritesAmount.amount);
      } else {
        setFavoritesNumber(0);
      }
    }
  }, [logged]);

  useEffect(() => {
    if (logged) {
      refetch();
      setFavoritesNumber(favoritesAmount?.amount || 0);
    }
  }, [favorites, favoritesAmount]);

  const [menuMobileActive, setMenuMobileActive] = useState(false);
  const onLinkClickCloseNavbar = () => {
    setMenuMobileActive(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };
  const navBar = useRef<HTMLElement>(null);

  if (location.pathname == '/signup' || location.pathname == '/login') {
    return <div />;
  }
  return (
    <nav
      ref={navBar}
      className={`${styles.uaNavbar} navbar navbar-expand-lg navbar-light sticky-top`}
    >
      <div className="container py-0 px-2 px-md-0">
        <Link className="navbar-brand me-md-3 " to="/">
          <img
            className=""
            src="https://www.unifyapply.com/static/media/logo_unify.1892d2c8c126ae1f4ad9.png"
          />
        </Link>
        <div className="mobile-actions ms-auto flex-row align-items-center me-2">
          <button className="btn me-2 fs-4 text-primary">
            {' '}
            <i className="bi bi-search" />
          </button>
          {!user ? (
            <button className="btn me-2 fs-4 text-primary">
              {' '}
              <i className="bi bi-person-circle" />
            </button>
          ) : (
            <button className=" ua-user btn rounded-circle bg-ua-light fw-bold p-1 d-flex align-items-center justify-content-center ">
              {userInitials(user)}
            </button>
          )}
        </div>
        <button
          className="navbar-toggler fs-2 border-0"
          onClick={() => {
            setMenuMobileActive(!menuMobileActive);
            window.scrollTo(0, 0);
          }}
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded={menuMobileActive ? true : false}
          aria-label="Toggle navigation"
        >
          {!menuMobileActive ? (
            <i className="bi bi-list animate__animated animate__fadeIn" />
          ) : (
            <i className="bi bi-x-lg animate__animated animate__fadeIn" />
          )}
        </button>

        <div
          className={`navbar-collapse position-relative collapse ${
            menuMobileActive ? 'show animate__animated animate__fadeInRight' : ''
          }`}
          id="navbarSupportedContent"
        >
          <ul className={`navbar-nav me-auto ${menuMobileActive ? ' py-3' : ''}`}>
            {dropdownShown && <div className={styles.overlay} />}
            {!location.pathname.includes('/home') &&
              data.map((menu, i) => {
                return <NavBarDropDown key={i} data={menu} closeNavBar={onLinkClickCloseNavbar} />;
              })}
          </ul>
          <div className="list-unstyled navbar-actions m-0 p-0 d-flex flex-row align-items-center position-relative">
            {!location.pathname.includes('/home') && (
              <Dropdown
                onToggle={(nextShow) =>
                  nextShow ? showDropdown('search') : hideDropdown('search')
                }
                drop={'start'}
                show={dropdownsShown['search']}
              >
                <Dropdown.Toggle
                  className={styles.searchIconContainer}
                  style={{ backgroundColor: 'transparent' }}
                >
                  <i className="bi bi-search fa-10x"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu className={styles.dropdownSearchMenu}>
                  <SearchMenu navigateTo={navigateTo} />
                </Dropdown.Menu>
              </Dropdown>
            )}
            {!user ? (
              <>
                <Link
                  className="btn btn-outline-primary ua-outline-primary rounded-pill px-4 py-2 fw-semibold me-0 me-md-2 my-3 my-md-0"
                  to="/login"
                >
                  Login
                </Link>
                <Link
                  className="btn btn-primary ua-primary rounded-pill px-4 py-2 my-3 my-md-0 fw-semibold"
                  to="/signup"
                >
                  Register
                </Link>
              </>
            ) : (
              <>
                <button
                  className={`btn me-2 d-none d-md-inline text-primary position-relative ${styles.favoriteButton}`}
                  onClick={navigateToFavorite}
                >
                  <i className="bi bi-heart fs-4" />
                  <span className="position-absolute top-50 start-75 translate-middle badge rounded-pill bg-ua-light text-primary">
                    <small>{favoritesNumber}</small>
                    <span className="visually-hidden">favorites</span>
                  </span>
                </button>
                {location.pathname.includes('/home') ? (
                  <div className="d-flex flex-column align-items-center">
                    <UserDashboardSideBarElements />
                    <div className={`nav-item ${styles.expertBox}`}>
                      <Link
                        to="/request-meeting"
                        className={`nav-link ${styles.expertBox}`}
                        aria-current="page"
                      >
                        <img src={Expert1} className={styles.expertIcon} />
                        <div className="d-flex flex-column">
                          <p className={`m-0 ${styles.name}`}> Patricia </p>
                          <p className={`m-0 ${styles.sub}`}> My expert</p>
                        </div>
                        <div className={`${styles.greenDot}`} />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <NavBarUserDropDown user={user} closeNavBar={onLinkClickCloseNavbar} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
